import React from 'react'

import {
  IndexLayoutOld,
  LoadingPage,
  routes,
  useGetWorkspaces,
  useUserProfile,
} from '@sketch/modules-common'

import { Link, Text, ErrorMessage } from '@sketch/components'

import {
  BackButton,
  BottomText,
  Description,
  ErrorWrapper,
  StyledIndexLayoutOld,
  StyledWorkspaceLogo,
  SwitchAccount,
  Wrapper,
} from './NoWorkspaceAccess.styles'
import { useGetPublicWorkspaceQuery } from '@sketch/gql-types'
import { SKETCH_WEBSITE } from '@sketch/env-config'
import { ReactComponent as ExclamationMarkIcon } from '@sketch/icons/exclamation-mark-triangle-64'
import { useHistory, useLocation } from 'react-router'
import { useSignInToUnauthorizedWorkspace } from '../../../sso/operations'

interface NoWorkspaceAccessProps {
  workspaceId: string
  redirectBackToCurrentUrl?: boolean
}

const getMemberCopy = (isMember: boolean, isSSO: boolean, email: string) => {
  const isSSOAndMemberCopy = `You're a member of this workspace with your account ${email}. Sign in with SSO to continue.`
  const isRegularAndMemberCopy = `You're a member of this workspace with your account ${email}. Sign in to continue.`
  const notMemberCopy = `You're not a member of this workspace with your current account ${email}. Sign out and try with a different account.`

  return isMember
    ? isSSO
      ? isSSOAndMemberCopy
      : isRegularAndMemberCopy
    : notMemberCopy
}
/**
 * User has no access to the workspace but he might be a member (or not)
 * This view will allow users to sign in to the proper account
 */
export const NoWorkspaceAccess: React.FCC<NoWorkspaceAccessProps> = props => {
  const { workspaceId, redirectBackToCurrentUrl = true } = props

  const history = useHistory()
  const location = useLocation()
  const { signIn } = useSignInToUnauthorizedWorkspace()

  const { data: userData } = useUserProfile(true)
  const email = userData?.me.personalIdentityEmail ?? userData?.me.email ?? ''

  const {
    data: publicWorkspaceData,
    loading,
    error,
  } = useGetPublicWorkspaceQuery({
    variables: {
      identifier: workspaceId,
    },
  })

  const { workspaces, error: workspacesError } = useGetWorkspaces({
    // Given we have a cache Workspace redirect in place, (see: packages/graphql-cache/src/redirects.js#27)
    // by fetching the user workspaces, it would set the cache in an incorrect state.
    //
    // This query will return all workspaces that the user profile belongs to, therefore we can't use it
    // as valid cache.
    fetchPolicy: 'no-cache',
  })

  if (loading) {
    return <LoadingPage />
  }

  /** The workspace doesn't exist at all so we render the generic error */
  if (error || workspacesError || !publicWorkspaceData) {
    return (
      <StyledIndexLayoutOld
        headerLink="no-workspace-access"
        ignoreWorkspaceMatch
      >
        <ErrorWrapper>
          <ErrorMessage
            icon={<ExclamationMarkIcon />}
            title="We cannot find the page you’re looking for."
            description={
              <Link
                to={routes.WORKSPACE_AGNOSTIC_SHARES.create({})}
                isUnderlined
              >
                Go to your documents view
              </Link>
            }
          />
        </ErrorWrapper>
      </StyledIndexLayoutOld>
    )
  }

  const { publicWorkspace } = publicWorkspaceData

  const isSSO = !!publicWorkspace.customer?.ssoEnabled
  const isMember = (workspaces ?? []).some(
    workspace => workspace.identifier === workspaceId
  )

  const buttonCopy = isMember
    ? isSSO
      ? 'Sign In With SSO'
      : 'Sign In'
    : 'Switch Account'

  const handleSwitchAccount = () => {
    signIn({
      workspace: publicWorkspace,
      // To redirect users after they sign in with U+P
      onCompleted: () => history.push(location),
      // To redirect users after they sign in with SSO
      redirectBackToCurrentUrl,
      ignoreUserProfile: !isMember,
    })
  }

  return (
    <IndexLayoutOld headerLink="no-workspace-access" ignoreWorkspaceMatch>
      <Wrapper>
        <StyledWorkspaceLogo
          workspaceName={publicWorkspace.name}
          src={publicWorkspace.avatar?.large ?? publicWorkspace.avatar?.small}
          size="64px"
        />
        <Text textStyle="header.primary.G">{publicWorkspace.name}</Text>

        <Description textStyle="copy.quaternary.standard.D">
          {getMemberCopy(isMember, isSSO, email)}
        </Description>

        <SwitchAccount variant="primary" onClick={handleSwitchAccount} fill>
          {buttonCopy}
        </SwitchAccount>

        <BackButton variant="secondary" to={routes.ENTRY.create({})} fill>
          Go Back
        </BackButton>
      </Wrapper>
      <BottomText textStyle="copy.tertiary.standard.D">
        <span>
          Need Help?{' '}
          <Link
            variant="primary"
            href={`${SKETCH_WEBSITE}/support/`}
            target="_blank"
            external
            isUnderlined
          >
            Get in touch
          </Link>{' '}
        </span>
      </BottomText>
    </IndexLayoutOld>
  )
}
